export interface Photo {
  /** The shrunken picture as base64 JPEG, ready to send. */
  base64: string;
  mime: 'image/jpeg';
  width: number;
  height: number;
  /** The pixels, for measuring the colour in the browser. They never leave the phone. */
  pixels: ImageData;
  /** A temporary local address to show the picture. Revoke it with URL.revokeObjectURL when done. */
  previewUrl: string;
}

function readAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new Error('Could not read the photo.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Turns whatever the camera produced (often 4000 pixels wide and several megabytes) into a picture of about
 * 1,000 pixels on its long side: quick to send on mobile data, and plenty to recognise a part.
 *
 * Redrawing it on a canvas also drops everything hidden in the original file, including the GPS position and
 * the camera details, so only the pixels are sent.
 */
export async function preparePhoto(file: File, maxEdge = 1024, quality = 0.85): Promise<Photo> {
  let bitmap: ImageBitmap;
  try {
    // Honour the camera's rotation, so a photo taken upright is not sent sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      throw new Error('This browser could not open that photo. Try taking it again.');
    }
  }

  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot process pictures.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not prepare the photo.'))), 'image/jpeg', quality),
  );
  return {
    base64: await readAsBase64(blob),
    mime: 'image/jpeg',
    width,
    height,
    pixels: ctx.getImageData(0, 0, width, height),
    previewUrl: URL.createObjectURL(blob),
  };
}
